import {
    Fragment,
    type ReactNode,
    type RefObject,
    useEffect,
    useRef,
    useState,
} from "react";
import classNames from "classnames";
import { BOARD_CARD_ATTRIBUTE } from "@design-system/Board/boardAttributes";
import { BOARD_TAB_STOP_ATTRIBUTE } from "@design-system/Board/boardSensors";
import { Menu } from "@design-system/Menu/Menu.lib";
import { Tag } from "@design-system/Tag/Tag";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { PlusIcon } from "@icons";
import { getTaskChipClassName, taskControlProps } from "./TaskChipButton";
import {
    TASK_CARD_FIELD_LABELS,
    type TaskCardEditField,
} from "./taskCardEdits";
import styles from "./TaskBoardChips.module.scss";

const ADD_FIELD_LABEL = "Add field";
const TAB_STOP_PROPS = { [BOARD_TAB_STOP_ATTRIBUTE]: "" };
const NO_FIELDS: readonly TaskCardEditField[] = [];

/** Whether focus is in the board card around `ref`'s element, kept in step as focus moves. */
const useIsCardFocusWithin = (ref: RefObject<HTMLElement | null>) => {
    const [isCardFocusWithin, setIsCardFocusWithin] = useState(false);
    useEffect(() => {
        const card = ref.current?.closest<HTMLElement>(
            `[${BOARD_CARD_ATTRIBUTE}]`,
        );
        if (!card) {
            return undefined;
        }
        setIsCardFocusWithin(card.contains(document.activeElement));
        const handleFocusIn = () => setIsCardFocusWithin(true);
        const handleFocusOut = ({ relatedTarget }: FocusEvent) =>
            setIsCardFocusWithin(
                relatedTarget instanceof Node && card.contains(relatedTarget),
            );
        card.addEventListener("focusin", handleFocusIn);
        card.addEventListener("focusout", handleFocusOut);
        return () => {
            card.removeEventListener("focusin", handleFocusIn);
            card.removeEventListener("focusout", handleFocusOut);
        };
    }, [ref]);
    return isCardFocusWithin;
};

/** The field picked from a card's Add field menu, kept by the card while its control is open. */
export interface TaskAddFieldState {
    field: TaskCardEditField | null;
    onFieldChange: (field: TaskCardEditField | null) => void;
}

interface TaskAddFieldChipProps {
    /** The card's shown, editable fields with no value, in card order. */
    emptyFields: readonly TaskCardEditField[];
    /** The editable fields with no value the view doesn't show on cards, in List's order. */
    unshownFields?: readonly TaskCardEditField[];
    /** The picked field's control, opened over the chip; `onEnd` once it closes. */
    renderControl: (field: TaskCardEditField, onEnd: () => void) => ReactNode;
    adding: TaskAddFieldState;
}

/**
 * The "+" tag after a card's tags (and their +N), never behind the +N: a
 * menu of the card's empty fields, those shown on cards then those not,
 * where picking one opens that field's control over the chip, as its
 * shortcut would. Shown while the card is hovered or holds focus; it always
 * takes its space. Tab reaches it from its card only (`tabIndex={-1}`
 * otherwise), so a hovered card's chip never takes Tab and the focused card
 * stays the board's one Tab stop.
 */
export const TaskAddFieldChip = ({
    emptyFields,
    unshownFields = NO_FIELDS,
    renderControl,
    adding,
}: Readonly<TaskAddFieldChipProps>) => {
    const slotRef = useRef<HTMLSpanElement>(null);
    const isCardFocusWithin = useIsCardFocusWithin(slotRef);
    const [isMenuMounted, setIsMenuMounted] = useState(false);
    const pickedFieldRef = useRef<TaskCardEditField | null>(null);
    const handleMenuOpenChange = (isOpen: boolean) => {
        if (isOpen) {
            setIsMenuMounted(true);
        }
    };
    /**
     * Opens the picked field's control a frame after the menu is gone. The
     * menu hands focus back to the chip after unmounting: opened sooner, the
     * control would lose focus to the chip (and a picker closes on that), and
     * a chip hidden sooner couldn't take it.
     */
    const handleMenuClosed = (isOpen: boolean) => {
        if (isOpen) {
            return;
        }
        const pickedField = pickedFieldRef.current;
        pickedFieldRef.current = null;
        requestAnimationFrame(() => {
            if (pickedField !== null) {
                adding.onFieldChange(pickedField);
            }
            setIsMenuMounted(false);
        });
    };

    return (
        <span
            ref={slotRef}
            className={styles.addField}
            data-active={isMenuMounted || adding.field !== null || undefined}
        >
            <Menu.Root
                onOpenChange={handleMenuOpenChange}
                onOpenChangeComplete={handleMenuClosed}
                trigger={
                    <Menu.Trigger
                        nativeButton
                        render={(triggerProps) => (
                            <Tooltip content={ADD_FIELD_LABEL}>
                                <button
                                    {...triggerProps}
                                    {...TAB_STOP_PROPS}
                                    {...taskControlProps("addField")}
                                    type="button"
                                    tabIndex={isCardFocusWithin ? 0 : -1}
                                    aria-label={ADD_FIELD_LABEL}
                                    className={classNames(
                                        getTaskChipClassName("chip"),
                                        styles.addFieldButton,
                                    )}
                                >
                                    <Tag
                                        size="sm"
                                        color="neutral"
                                        outline
                                        numeric
                                        className={styles.addFieldTag}
                                    >
                                        <PlusIcon aria-hidden />
                                    </Tag>
                                </button>
                            </Tooltip>
                        )}
                    />
                }
            >
                {[
                    { label: "Shown on cards", fields: emptyFields },
                    { label: "Not shown on cards", fields: unshownFields },
                ]
                    .filter(({ fields }) => fields.length > 0)
                    .map(({ label, fields }) => (
                        <Menu.Group key={label}>
                            <Menu.GroupLabel>{label}</Menu.GroupLabel>
                            {fields.map((field) => (
                                <Menu.Item
                                    key={field}
                                    onClick={() => {
                                        pickedFieldRef.current = field;
                                    }}
                                >
                                    {TASK_CARD_FIELD_LABELS[field]}
                                </Menu.Item>
                            ))}
                        </Menu.Group>
                    ))}
            </Menu.Root>
            {adding.field !== null && (
                <Fragment key={adding.field}>
                    {renderControl(adding.field, () =>
                        adding.onFieldChange(null),
                    )}
                </Fragment>
            )}
        </span>
    );
};
